import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ElementRef,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Cache } from 'core/models/cache';
import { Course } from 'core/models/course';
import { Profile } from 'core/models/agent';
import { ProfileService } from 'core/services/profile.service';
import { StoreService } from 'core/services/store.service';
import { VIEWMODES } from '../assets/constants';
import { CoursesListService } from './courses-list.service';
import { CoursesFiltersQuestionsService } from './courses-filters-questions.service';
import { Client } from 'core/models/client';
import { DistributorsService } from 'core/services/distributors.service';
import { PlansService } from 'core/services/plans.service';
import { Observable, Subscription } from 'rxjs';
import { QuestionBase } from 'core/components/forms';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'lms-courses-list',
  templateUrl: './courses-list.component.html',
  styleUrls: ['./courses-list.component.scss'],
  providers: [CoursesListService, CoursesFiltersQuestionsService]
})
export class CoursesListComponent implements OnInit, OnChanges {
  public coursesTableListId = 'COURSES_TABLE_LIST';
  @Input() listId: any = 'COURSES_LIST';
  @Input() courses: Course[] = [];
  @Input() client: Client;
  @Input() options: any;
  @Input() selectable: boolean;
  @Input() bulkActions: any[] = [];

  @Output() public courseEvent = new EventEmitter<any>();
  @Output() public courseBulkAction = new EventEmitter<any>();
  @ViewChild('filtersPanel', { static: false }) public filtersPanel: ElementRef;
  @ViewChild('coursesTable', { static: false }) public coursesTable: ElementRef;

  public user: Profile;
  public coursesCache = new Cache();
  public dataSource: any;
  public filterQuestions: QuestionBase<any>[];
  public noCourses: boolean = true;

  public viewModes = VIEWMODES;
  public viewMode: string = VIEWMODES.cards;
  public showFilters: boolean = false;
  public toolbarConfig: any;

  public listSubscription: Subscription;

  public selectAll: boolean = false;
  public selectedCourses: Map<string, any> = new Map();

  public formAction = null;
  private filterSubscription: Subscription = new Subscription();
  private loggedUserSubscription: Subscription = new Subscription();

  constructor(
    public translate: TranslateService,
    public profileService: ProfileService,
    public distributorsService: DistributorsService,
    public coursesListService: CoursesListService,
    public coursesFiltersQuestionsService: CoursesFiltersQuestionsService,
    public plansService: PlansService,
    public elementRef: ElementRef,
    private activatedRoute: ActivatedRoute
  ) {
    this.loggedUserSubscription = this.profileService.getLoggedUser$().subscribe((user) => {
      this.user = user;
    });
  }

  async ngOnInit() {
    this.filterSubscription = this.getFormActionFilter().subscribe();
    this.toolbarConfig = {
      list: {
        listId: this.listId,
        hideItemsSelector: true,
        hidePrint: true,
        skeletor: true,
        skipEncoding: true
      }
    };
    this.coursesCache.data = this.courses ? this.courses : [];
    const options = {
      ...this.options,
      selectionHandler: (data) => this.selectionHandler(data)
    };
    this.dataSource = this.coursesListService.getDatasource(this.listId, options, this.coursesCache);
    this.setFilterQuestions();
    this.noCourses = this.dataSource.docs.length === 0;
    StoreService.get(this.listId);
    this.listSubscription = StoreService.get(this.listId).subscribe((dataSource) => (this.dataSource = dataSource));
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['courses'] && this.dataSource) {
      this.setSelectAll(false);
      this.coursesCache.data = this.courses;
      await this.dataSource.refreshData({ page: 1 });
      StoreService.update(this.listId, this.dataSource);
    }
  }

  public ngOnDestroy(): void {
    this.listSubscription.unsubscribe();
    this.filterSubscription.unsubscribe();
    this.loggedUserSubscription.unsubscribe();
  }

  setViewMode(viewMode) {
    this.viewMode = viewMode;
  }

  emitCourseEvent(event: any) {
    this.courseEvent.emit(event);
  }

  setFilterQuestions() {
    this.filterQuestions = this.coursesFiltersQuestionsService.getFilterQuestions(
      this.options.filterList,
      this.dataSource
    );
  }

  resetFilters() {
    this.setFilterQuestions();
    this.filter({});
    this.filtersPanel.nativeElement.focus();
  }

  async filter(userFilters) {
    const filters = { ...userFilters };
    await this.dataSource.refreshData({ filters, page: 1 });
    StoreService.update(this.listId, this.dataSource);
    this.setSelectAll(false);
  }

  showFilterPanel(isClicked: boolean, ev: any) {
    const relatedTarget = ev?.relatedTarget;
    if (relatedTarget && this.filtersPanel.nativeElement.contains(relatedTarget)) {
      return;
    }
    if (isClicked) {
      setTimeout(() => {
        this.filtersPanel.nativeElement.focus();
      });
    }
    this.showFilters = isClicked;
  }

  selectAction(action) {
    this.courseBulkAction.emit({ action, selectedCourses: this.selectedCourses });
  }

  /*table selection functions*/
  onSelectionChanged(selection) {
    const { selected = [] } = selection || {};
    this.selectedCourses.clear();
    this.selectAll = false;
    selected.forEach((element) => {
      this.onCourseSelectionChanged({ selected: true, data: element });
    });
  }

  onCourseSelectionChanged(eventDetail) {
    const { selected, data } = eventDetail;
    if (selected) {
      this.selectedCourses.set(data.id, data);
    } else {
      this.selectedCourses.delete(data.id);
    }
    this.selectAll = this.selectedCourses.size === this.dataSource.total;
  }

  onFiltersChanged() {
    this.setSelectAll(false);
  }

  setSelectAll(flag) {
    this.selectedCourses.clear();
    if (flag) {
      this.dataSource.docs.forEach((element: any) => {
        this.selectedCourses.set(element.id, element);
      });
    }
    this.selectAll = flag;
    if (this.coursesTable) this.coursesTable.nativeElement.setAllSelected(flag);
  }

  selectionHandler(rowData) {
    if (rowData) {
      return this.selectAll || (this.selectedCourses && this.selectedCourses.has(rowData.id));
    }
    return this.selectAll;
  }

  onDatasourceUpdate() {
    StoreService.update(this.listId, this.dataSource);
    this.setSelectAll(false);
  }

  /**
   * Get the information of the dropdowns
   * @private
   */
  private getFormActionFilter(): Observable<any> {
    return this.activatedRoute.data.pipe(
      tap((data) => {
        this.formAction = data['action'];
      })
    );
  }
}
